import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import type { TextFieldProps } from "@mui/material";
import { CircularProgress, InputAdornment, TextField, TextFieldVariants, Tooltip } from "@mui/material";
import { Variant } from "@mui/material/styles/createTypography";
import { mergeSx } from "helpers/utils";
import type { ReactNode } from "react";
import { forwardRef } from "react";
import { useTranslation } from "react-i18next";

import variables from "../../../assets/styles/_variables.module.scss";

export const disabledTooltipStyle = {
    "& .MuiTooltip-tooltip": {
        backgroundColor: "#FBFEE3",
        color: "#696F3F",
        border: "1px solid #EAEAEA",
        ".MuiTooltip-arrow": {
            color: "#FBFEE3",
        },
        ".MuiTooltip-arrow::before": {
            border: "1px solid #EAEAEA",
        },
    },
};

const textfieldStyle = {
    ".MuiFormHelperText-root": {
        color: "#d32f2f",
    },
    "&.MuiTextField-root": {
        ".MuiOutlinedInput-root": {
            fontFamily: "Poppins",
            borderRadius: variables["border-radius"],
            "&:hover": {
                "& > fieldset": {
                    borderColor: variables["secondary-purple"],
                },
            },
            "& > fieldset": {
                borderColor: variables["border-grey"],
            },
            "&.Mui-error > fieldset": {
                borderColor: "#9B5067",
            },
            "&.Mui-focused": {
                "& > fieldset": {
                    borderColor: variables["primary-purple"],
                },
            },
            ".MuiOutlinedInput-input": {
                padding: "8px 0 8px 16px",
                fontSize: "14px",
            },
            "&.Mui-error": {
                backgroundColor: "#FCEFF3",
                borderColor: "#9B5067",
            },
        },
        ".MuiFormHelperText-root ": {
            color: "#9B5067",
            display: "flex",
            alignItems: "center",
            gap: "4px",
            fontFamily: "Poppins",
            fontSize: "0.75rem",
            paddingTop: "2px",
            lineHeight: "1",
            marginLeft: "4px",
            "&::before": {
                content:
                    'url("data:image/svg+xml;utf8,%3Csvg%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2015%2015%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20%3E%20%3Cpath%20d%3D%22M6.43148%202.41226L1.13773%2011.2498C1.02858%2011.4388%200.970831%2011.6531%200.97022%2011.8713C0.969609%2012.0896%201.02616%2012.3042%201.13424%2012.4938C1.24233%2012.6835%201.39818%2012.8415%201.58629%2012.9522C1.77441%2013.0629%201.98823%2013.1224%202.20648%2013.1248H12.794C13.0122%2013.1224%2013.226%2013.0629%2013.4142%2012.9522C13.6023%2012.8415%2013.7581%2012.6835%2013.8662%2012.4938C13.9743%2012.3042%2014.0308%2012.0896%2014.0302%2011.8713C14.0296%2011.6531%2013.9719%2011.4388%2013.8627%2011.2498L8.56898%202.41226C8.45756%202.22858%208.30068%202.07671%208.11347%201.97131C7.92627%201.86592%207.71506%201.81055%207.50023%201.81055C7.28539%201.81055%207.07418%201.86592%206.88698%201.97131C6.69978%202.07671%206.5429%202.22858%206.43148%202.41226V2.41226Z%22%20stroke%3D%22%239B5067%22%20stroke-width%3D%221.03448%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20%2F%3E%20%3Cpath%20d%3D%22M7.5%205.625V8.125%22%20stroke%3D%22%239B5067%22%20stroke-width%3D%221.03448%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20%2F%3E%20%3Cpath%20d%3D%22M7.5%2010.625H7.5066%22%20stroke%3D%22%239B5067%22%20stroke-width%3D%221.03448%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20%2F%3E%20%3C%2Fsvg%3E")',
                display: "flex",
                transform: "scale(0.8)",
            },
        },
    },
};

const smallTextFieldStyle = {
    "&.MuiTextField-root": {
        ".MuiOutlinedInput-root": {
            ".MuiOutlinedInput-input": {
                padding: "3px 0 3px 8px !important",
                fontSize: "14px",
            },
        },
    },
};

const disabledTextfieldStyle = {
    "&.MuiTextField-root": {
        ".MuiOutlinedInput-root": {
            "&:hover": {
                "& > fieldset": {
                    borderColor: "none",
                },
            },
        },
    },
};

type GetEndAdornmentParams = {
    disabled?: boolean;
    disabledText?: string;
    disabledFieldLabel: string;
    hideDisabledLockIcon?: boolean;
    loading?: boolean;
    icon?: JSX.Element;
    fallbackEndAdornment?: ReactNode;
};

function getEndAdornment({
    disabled,
    disabledText,
    disabledFieldLabel,
    hideDisabledLockIcon,
    loading,
    icon,
    fallbackEndAdornment,
}: GetEndAdornmentParams): ReactNode {
    if (disabled) {
        if (hideDisabledLockIcon) {
            if (loading) {
                return <CircularProgress sx={{ color: variables["primary-purple"] }} size={18} />;
            }
            if (icon) {
                return <InputAdornment position="end">{icon}</InputAdornment>;
            }
            return fallbackEndAdornment;
        }

        return (
            <Tooltip arrow title={disabledText ?? disabledFieldLabel} PopperProps={{ sx: disabledTooltipStyle }}>
                <LockOutlinedIcon
                    sx={{
                        color: "#888",
                        height: "15px",
                        width: "15px",
                        marginRight: 0,
                        cursor: "pointer",
                    }}
                />
            </Tooltip>
        );
    }

    if (loading) {
        return <CircularProgress sx={{ color: variables["primary-purple"] }} size={18} />;
    }

    if (icon) {
        return <InputAdornment position="end">{icon}</InputAdornment>;
    }

    return fallbackEndAdornment;
}

type StyledTextFieldProps = {
    trueInputRef?: any;
    disabledText?: string;
    /** When disabled, show the tooltip on the field hover instead of a lock icon adornment. */
    hideDisabledLockIcon?: boolean;
    inputType?: string;
    loading?: boolean;
    icon?: JSX.Element;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
} & TextFieldProps;

const StyledTextField = forwardRef<any, StyledTextFieldProps>(function StyledTextField(
    props: StyledTextFieldProps,
    ref,
) {
    const [t] = useTranslation();
    const {
        inputType,
        trueInputRef,
        disabledText,
        disabled,
        hideDisabledLockIcon,
        size,
        sx,
        loading,
        icon,
        onChange,
        InputProps: inputProps,
        ...rest
    } = props;

    const endAdornment = getEndAdornment({
        disabled,
        disabledText,
        disabledFieldLabel: t("Generic.DisabledField"),
        hideDisabledLockIcon,
        loading,
        icon,
        fallbackEndAdornment: inputProps?.endAdornment,
    });

    const textField = (
        <TextField
            type={inputType}
            inputRef={trueInputRef}
            ref={ref}
            disabled={disabled}
            onChange={onChange}
            InputProps={{
                ...inputProps,
                endAdornment,
            }}
            {...rest}
            sx={mergeSx(
                textfieldStyle,
                sx,
                disabled && disabledTextfieldStyle,
                size === "small" && smallTextFieldStyle,
            )}
        />
    );

    if (disabled && hideDisabledLockIcon) {
        return (
            <Tooltip
                arrow
                title={disabledText ?? t("Generic.DisabledField")}
                PopperProps={{ sx: disabledTooltipStyle }}
            >
                <span style={{ display: "inline-block", width: "100%" }}>{textField}</span>
            </Tooltip>
        );
    }

    return textField;
});

export default StyledTextField;
